import React from "react";
import PropTypes from "prop-types";
import classcat from "classcat";
import { Typography, Box } from "@material-ui/core";
import { Check as CheckIcon } from "@material-ui/icons";
import { BenefitTypeEnum } from "v2/domain/common/entities/BenefitType";
import { translate as t } from "../../../infrastructure/translate/localization";
import WarningFlexAdvice from "./warningFlexAdvice";
import "./benefitCard.scss";
import Tag from "./tag/Tag";
import TooltipCard from "../tooltipCard/TooltipCard";
import Badge from "../Badge";

export const getButtonText = ({ hasWarning, isCustom, active }) => {
  if (!hasWarning) {
    return active ? t("actions.deactivate") : t("actions.active");
  }
  if (isCustom) {
    return t("v2.benefits.custom.create");
  }
  return t("actions.config");
};

const badgeConfig = {
  novelty: {
    text: t("common-messages.novelty"),
    type: "novelty",
  },
  processing: {
    text: t("common-messages.processing"),
    type: "processing",
  },
  beta: {
    text: t("v2.common.beta"),
    type: "beta",
  },
};

const BenefitCard = ({
  title,
  image,
  comingSoon,
  requestInfo,
  showDetail,
  tooltipText,
  advanced,
  active,
  handleActiveToggle,
  passeSocialBenefitData,
  handleAdvance,
  isDisabled,
  showDisableFlexMessage,
  country,
  isV2Benefit,
  hasV2Configuration,
  warningTooltipText,
  hasV2Screen,
  handleSeeMore,
  requestInfoButtonAction,
  requested,
  badgeStatus,
  requestedButtonAction,
  tags,
  benefit,
  beta,
  isCustom,
}) => {
  if (comingSoon) {
    return (
      <div className="benefit-card">
        <div className="benefit-card-title">
          <p className="benefit-title">{title}</p>
          {tooltipText && (
            <TooltipCard
              title={title}
              country={country}
              tooltipText={tooltipText}
            />
          )}
        </div>
        <p className="coming-soon">{t("benefits-section.coming-soon")}</p>
      </div>
    );
  }
  if (requestInfo) {
    return (
      <div className="benefit-card">
        <div className="benefit-card-title">
          <p className="benefit-title">{title}</p>
          {tooltipText && (
            <TooltipCard
              title={title}
              country={country}
              tooltipText={tooltipText}
            />
          )}
        </div>
        {badgeStatus && (
          <div className="badge-container">
            <Badge
              text={badgeConfig[badgeStatus].text}
              type={badgeConfig[badgeStatus].type}
            />
          </div>
        )}
        {image && <img src={image} className="disabled" alt="benefit" />}
        <div className="button-container">
          {requestInfoButtonAction ? (
            <button
              type="button"
              aria-label={`See more ${benefit}`}
              className={classcat([
                "button-container",
                "btn-benefit",
                "left",
                "right",
                "requestable",
              ])}
              onClick={requestInfoButtonAction}
            >
              {t("actions.request-info")}
            </button>
          ) : (
            <a
              className={classcat([
                "button-container",
                "btn-benefit",
                "left",
                "right",
                "requestable",
              ])}
              href="mailto:info@cobee.io?Subject=Me%20gustaría%20recibir%20más%20información"
            >
              {t("actions.request-info")}
            </a>
          )}
        </div>
      </div>
    );
  }
  if (showDetail) {
    return (
      <div className="benefit-card">
        <div className="benefit-card-title">
          <p className="benefit-title">{title}</p>
          {tooltipText && (
            <TooltipCard
              title={title}
              country={country}
              tooltipText={tooltipText}
            />
          )}
        </div>

        {image && <img src={image} alt="benefit" />}
        <div className="button-container">
          <button
            type="button"
            className={classcat([
              "button-container",
              "btn-benefit",
              "left",
              "right",
            ])}
            onClick={handleAdvance}
          >
            {t("actions.show-detail")}
          </button>
        </div>
      </div>
    );
  }
  if (advanced) {
    return (
      <div className="benefit-card">
        <div className="benefit-card-title">
          <p className="benefit-title">{title}</p>
          {tooltipText && (
            <TooltipCard
              title={title}
              country={country}
              tooltipText={tooltipText}
            />
          )}
        </div>
        {showDisableFlexMessage && (
          <WarningFlexAdvice
            text={t("benefits-section.warning-flex-message")}
          />
        )}
        {image && <img src={image} alt="benefit" />}
        <div className="button-container">
          <button
            type="button"
            className={classcat([
              "button-container",
              "btn-benefit",
              "left",
              { disabled: !active },
            ])}
            onClick={handleActiveToggle}
          >
            {active ? t("actions.deactivate") : t("actions.active")}
          </button>
          <button
            type="button"
            className={classcat([
              "button-container",
              "btn-benefit",
              "right",
              { disabled: !active },
            ])}
            onClick={handleAdvance}
          >
            {t("actions.advanced")}
          </button>
        </div>
      </div>
    );
  }

  if (beta) {
    return (
      <div className="benefit-card">
        {badgeStatus && (
          <div className="badge-container">
            <Badge
              text={badgeConfig[badgeStatus].text}
              type={badgeConfig[badgeStatus].type}
            />
          </div>
        )}
        <div className="benefit-card-title">
          <p className="benefit-title">{title}</p>
        </div>
        <img
          src={image}
          alt="benefit"
          className={isDisabled ? "disabled" : undefined}
          style={{
            filter: `grayscale(${active ? 0 : 100}%)`,
          }}
        />
        <div className="button-container">
          <button
            type="button"
            className={classcat([
              "button-container",
              "btn-benefit",
              "beta-benefit",
              "left",
              "right",
            ])}
            onClick={requestedButtonAction}
          >
            {getButtonText({
              hasWarning: true,
              isCustom,
            })}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={classcat(["benefit-card", { disabled: isDisabled }])}>
      {badgeStatus && (
        <div className="badge-container">
          <Badge
            text={badgeConfig[badgeStatus].text}
            type={badgeConfig[badgeStatus].type}
          />
        </div>
      )}
      {tags?.map((tag) => (
        <Tag key={tag} tag={t(`common-messages.${tag}`)} />
      ))}
      <div className="benefit-card-title">
        <p className="benefit-title">{title}</p>
        {tooltipText && (
          <TooltipCard
            title={title}
            country={country}
            tooltipText={tooltipText}
            warningTooltipText={warningTooltipText}
            hasWarning={isV2Benefit && !hasV2Configuration}
          />
        )}
      </div>
      {showDisableFlexMessage && (
        <WarningFlexAdvice text={t("benefits-section.warning-flex-message")} />
      )}
      {image && (
        <img
          src={image}
          alt={title}
          className={isDisabled ? "disabled" : undefined}
          style={{
            filter: `grayscale(${active ? 0 : 100}%)`,
          }}
        />
      )}
      {hasV2Configuration && handleActiveToggle ? (
        <div className="button-container">
          <button
            type="button"
            className={classcat([
              "button-container",
              "btn-benefit",
              "left",
              { disabled: isV2Benefit ? false : !active },
            ])}
            onClick={handleActiveToggle}
          >
            {getButtonText({
              hasWarning: !hasV2Configuration,
              active,
            })}
          </button>
        </div>
      ) : (
        <>
          {passeSocialBenefitData && (
            <Box p={2}>
              <Typography variant="subtitle2" align="center">
                {passeSocialBenefitData.title}
              </Typography>
              <Typography variant="caption" align="center">
                {passeSocialBenefitData.text}
              </Typography>
            </Box>
          )}
          {handleActiveToggle && (
            <div className="button-container">
              <button
                type="button"
                aria-label={`See more ${benefit}`}
                className={classcat([
                  "button-container",
                  "btn-benefit",
                  "left",
                  "right",
                  { disabled: isV2Benefit ? false : !active },
                ])}
                disabled={isDisabled}
                onClick={isV2Benefit ? () => {} : handleActiveToggle}
              >
                {getButtonText({
                  hasWarning: isV2Benefit && !hasV2Configuration,
                  active,
                })}
              </button>
            </div>
          )}
          {hasV2Screen && (
            <div className="button-container">
              <button
                aria-label={`See more ${benefit}`}
                type="button"
                className={classcat([
                  "button-container",
                  "btn-benefit",
                  "left",
                  "right",
                  { disabled: !active },
                ])}
                disabled={isDisabled}
                onClick={handleSeeMore}
              >
                {t("actions.see-more")}
              </button>
            </div>
          )}
          {requested && (
            <button
              className="button-container"
              onClick={() =>
                requestedButtonAction ? requestedButtonAction() : null
              }
              type="button"
            >
              <div
                className={classcat([
                  "button-container",
                  "info-text-container",
                ])}
              >
                {t("benefits.status.requested")}
                <CheckIcon className="check-icon" fontSize="24" />
              </div>
            </button>
          )}
        </>
      )}
    </div>
  );
};

BenefitCard.defaultProps = {
  comingSoon: false,
  requestInfo: false,
  active: false,
  hasV2Configuration: false,
  showDetail: false,
  tooltipText: "",
  advanced: false,
  isDisabled: false,
  showDisableFlexMessage: false,
  handleAdvance: () => {},
  country: "ES",
  isV2Benefit: false,
  warningTooltipText: "",
  hasV2Screen: false,
  handleSeeMore: () => {},
  requestInfoButtonAction: undefined,
  passeSocialBenefitData: undefined,
  badgeStatus: "",
  requested: false,
  requestedButtonAction: undefined,
  tags: undefined,
  benefit: BenefitTypeEnum.MEAL_BENEFIT,
  beta: false,
  isCustom: false,
};

BenefitCard.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  active: PropTypes.bool,
  comingSoon: PropTypes.bool,
  requestInfo: PropTypes.bool,
  showDetail: PropTypes.bool,
  tooltipText: PropTypes.string,
  advanced: PropTypes.bool,
  isDisabled: PropTypes.bool,
  showDisableFlexMessage: PropTypes.bool,
  handleActiveToggle: PropTypes.func,
  handleAdvance: PropTypes.func,
  country: PropTypes.string,
  hasV2Configuration: PropTypes.bool,
  isV2Benefit: PropTypes.bool,
  warningTooltipText: PropTypes.string,
  hasV2Screen: PropTypes.bool,
  handleSeeMore: PropTypes.func,
  requestInfoButtonAction: PropTypes.func,
  passeSocialBenefitData: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
  }),
  badgeStatus: PropTypes.string,
  requested: PropTypes.bool,
  requestedButtonAction: PropTypes.func,
  tags: PropTypes.arrayOf(PropTypes.string),
  benefit: PropTypes.string,
  beta: PropTypes.bool,
  isCustom: PropTypes.bool,
};

export default BenefitCard;
